import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CompiereDataGridRequestJSON, CompiereDataGridType } from '@compiere-ws/models/compiere-data-json';
import { GridPreference, GridPreferencesService } from '@compiere-ws/services/grid-preferences/grid-preferences.service';
import { WidgetCenterService } from '@compiere-ws/services/widget-center/widget-center.service';
import { InfoDialogType } from '@iupics-components/specific/window/info-dialog/info-dialog.component';
import SpecificWindowUiComponent from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import UniversalFilterStandaloneUiComponent from '@iupics-components/standard/menu/universal-filter-standalone-ui/universal-filter-standalone-ui.component';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { IupicsWidget } from '@iupics-manager/models/iupics-widget';
import { AbstractWidgetComponent } from '@iupics/modules/iupics-widgets/abstract-widget.component';
import { TranslateModule } from '@ngx-translate/core';
import { ThemeService } from '@web-desktop/controllers/theme.service';
import { cloneDeep } from 'lodash';
import { catchError, finalize, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { ComboOption, WidgetEditorComboComponent } from './widget-editor-combo.component';
import { DashboardService } from '@compiere-ws/services/dashboard/dashboard.service';
import { IupicsDashboardCategory } from '@iupics-manager/models/iupics-dashboard';
import {
  formatNumber,
  levelState,
  parseDisplayOptions,
  serializeDisplayOptions,
  WidgetAlertDirection,
  WidgetDisplayOptions,
  WidgetLegend,
  WidgetNumberFormat,
} from '@iupics/modules/iupics-widgets/widget-display';
import { EditorColumn, EditorInfo, EditorTab, LibraryWidget, WidgetEditorLookupService } from './widget-editor-lookup.service';
import {
  AggregateOperator,
  applyType,
  COLUMN_KIND_ICONS,
  columnKind,
  describeFilters,
  FilterPhrase,
  valueColumnsFirst,
  clearColumns,
  CountListViewType,
  dataKey,
  DEFAULT_COLORS,
  duplicate,
  fromSavedView,
  isDirty,
  MIN_REFRESH_SECONDS,
  newWidget,
  operatorsFor,
  setSource,
  sourceOf,
  typeMeta,
  validate,
  WIDGET_TYPES,
  WidgetErrors,
  WidgetSource,
  WidgetTypeMeta,
  WidgetViewType,
} from './widget-editor.model';

/** components a "generic" widget can show (the other widget components belong to a type) */
const TYPED_COMPONENTS = ['CountListWidgetUiComponent', 'ChartWidgetComponent', 'GridWidgetComponent', 'MetabaseWidgetComponent'];
/** preview refreshed this long after the last change */
const PREVIEW_DELAY_MS = 400;
/** fields whose problems stop the preview (nothing to read yet) */
const PREVIEW_FIELDS = ['windowId', 'tabId', 'tableId', 'iframe', 'angularClass', 'categoryColumnId', 'serieColumnId', 'aggregateColumnId', 'aggregateOperator'];
export const REFRESH_PRESETS = [0, 60, 300, 900, 3600];

/** fields in page order: the first one with a problem is highlighted as the next to fill */
const FORM_ORDER = ['name', 'windowId', 'tabId', 'tableId', 'iframe', 'angularClass', 'categoryColumnId', 'serieColumnId', 'aggregateColumnId', 'aggregateOperator', 'recordLimit', 'precision', 'alertLevel', 'refreshRate'];

/** form sections, in page order, and the fields each one holds (navigation state, focus) */
export type EditorSection = 'type' | 'info' | 'source' | 'values' | 'display' | 'access';
const SECTION_FIELDS: Record<EditorSection, string[]> = {
  type: ['widgetType'],
  info: ['name', 'description'],
  source: ['source', 'windowId', 'tabId', 'tableId', 'iframe', 'angularClass'],
  values: ['defaultView', 'categoryColumnId', 'serieColumnId', 'aggregateColumnId', 'aggregateOperator', 'recordLimit'],
  display: ['measureUnit', 'precision', 'multiplier', 'normalLevel', 'alertLevel', 'refreshRate'],
  access: [],
};

/**
 * Widget editor (form "WidgetEditor"): one page to create or change a dashboard widget, with a live preview.
 * Own form (no dictionary fields): the type decides which settings are shown (widget-editor.model.ts), lookups come
 * from /widgetEditor/* with the role access applied. Hosted in a workspace tab, or in a modal by the dashboard
 * (widget-ui) and the saved views of a grid (favorite-selector): those read widget / updateInitialUFEmitter
 * (> 0 saved, 0 deleted). Details: docs/widgets/README.md.
 */
@Component({
  selector: 'iu-widget-editor-ui',
  templateUrl: './widget-editor-ui.component.html',
  styleUrls: ['./widget-editor-ui.component.scss'],
  standalone: true,
  imports: [DatePipe, NgClass, NgTemplateOutlet, TranslateModule, WidgetEditorComboComponent, UniversalFilterStandaloneUiComponent],
})
export default class WidgetEditorUiComponent extends SpecificWindowUiComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('previewHost', { read: ViewContainerRef, static: true }) previewHost: ViewContainerRef;
  /** scrolling part of the page (preview + form) */
  @ViewChild('body', { static: true }) bodyRef: ElementRef<HTMLElement>;
  readonly #zone = inject(NgZone);
  #unlistenScroll: (() => void) | undefined;

  /** widget to open (dashboard); after a save, the saved widget */
  @Input() widget: IupicsWidget;
  /** saved view of a grid the widget is made from (favorite-selector) */
  @Input() gridPref: GridPreference;
  @Output() closeEmitter = new EventEmitter<Event>();
  /** > 0: saved (its id), 0: deleted */
  @Output() updateInitialUFEmitter = new EventEmitter<number>();

  readonly #widgets = inject(WidgetCenterService);
  readonly #lookup = inject(WidgetEditorLookupService);
  readonly #themes = inject(ThemeService);
  readonly #gridPreferences = inject(GridPreferencesService);
  readonly #cd = inject(ChangeDetectorRef);

  readonly types = WIDGET_TYPES;
  readonly refreshPresets = REFRESH_PRESETS;
  readonly CountListViewType = CountListViewType;
  isAdmin = false;
  #owner = { roleId: 0, userId: 0 };

  /** widget being edited, and as last saved (unsaved changes) */
  readonly draft = signal<IupicsWidget>(null);
  readonly #saved = signal<IupicsWidget>(null);
  readonly saving = signal(false);
  /** problems shown on every field after a save attempt, else on the fields changed */
  readonly showErrors = signal(false);
  readonly #touched = signal<Set<string>>(new Set());

  readonly meta = computed<WidgetTypeMeta>(() => typeMeta(this.draft()?.widgetType));
  readonly source = computed<WidgetSource>(() => (this.draft() ? sourceOf(this.draft()) : 'tab'));
  readonly savedView = computed(() => !!this.draft() && fromSavedView(this.draft()));
  readonly errors = computed<WidgetErrors>(() => (this.draft() ? validate(this.draft(), this.aggregateColumn()) : {}));
  /** sections shown for the type, with what is left to fill (navigation bar) */
  readonly sections = computed(() => {
    const m = this.meta();
    const e = this.errors();
    const ids: EditorSection[] = ['type', 'info', 'source'];
    if (m.aggregate || m.category) ids.push('values');
    if (m.format || m.thresholds || m.family !== 'generic') ids.push('display');
    if (this.isAdmin) ids.push('access');
    return ids.map((id) => {
      const todo = SECTION_FIELDS[id].filter((f) => !!e[f]).length;
      const label = id === 'values' ? (m.family === 'count' ? 'count' : 'chart') : id;
      // optional sections (display, sharing) have no "done" mark
      const optional = ['display', 'access'].includes(id);
      return { id, label, todo, optional, done: !todo && !optional };
    });
  });
  /** required steps done / required steps (stepper) */
  readonly stepsDone = computed(() => this.sections().filter((s) => s.done).length);
  readonly stepsRequired = computed(() => this.sections().filter((s) => !s.optional).length);

  /** step number of a section (1-based, as in the stepper) */
  sectionNumber(id: EditorSection): number {
    return this.sections().findIndex((s) => s.id === id) + 1;
  }

  /** step number of the section holding a field (preview checklist) */
  fieldStep(field: string): number {
    const id = (Object.keys(SECTION_FIELDS) as EditorSection[]).find((s) => SECTION_FIELDS[s].includes(field));
    return id ? this.sectionNumber(id) : 0;
  }

  /** next field to fill, in page order (highlighted) */
  readonly nextField = computed(() => {
    const e = this.errors();
    return FORM_ORDER.find((f) => !!e[f]) ?? null;
  });

  /** type cards grouped: indicators (count, grid), charts, others (web page, component) */
  readonly typeGroups = computed(() => {
    const groups: { key: string; items: WidgetTypeMeta[] }[] = [
      { key: 'indicators', items: [] },
      { key: 'charts', items: [] },
      { key: 'others', items: [] },
    ];
    for (const t of this.typeChoices()) {
      const g = t.family === 'chart' ? 1 : t.family === 'count' || t.family === 'grid' ? 0 : 2;
      groups[g].items.push(t);
    }
    return groups.filter((g) => g.items.length);
  });

  typeIndex(t: WidgetTypeMeta): number {
    return this.typeChoices().indexOf(t);
  }

  /** what the widget will show, in a few lines (under the preview) */
  readonly willShow = computed(() => {
    const w = this.draft();
    const m = this.meta();
    if (!w) return [];
    const t = (key: string, params?: object) => this.translateService.instant(key, params);
    const rows: { label: string; value: string; missing?: boolean }[] = [];
    rows.push({ label: t('widgetEditor.sections.type'), value: t('widgetEditor.types.' + w.widgetType) });
    const src = this.source();
    if (src === 'tab') {
      const win = this.windowOption()?.name;
      const tab = this.tabOption()?.name?.trim();
      rows.push({ label: t('widgetEditor.sections.source'), value: win ? (tab ? win + ' \u203a ' + tab : win) : '-', missing: !win || !tab });
    } else if (src === 'table') {
      const table = this.tableOption()?.name;
      rows.push({ label: t('widgetEditor.sections.source'), value: table ?? '-', missing: !table });
    } else if (src === 'url') {
      rows.push({ label: t('widgetEditor.sections.source'), value: w.iframe || '-', missing: !w.iframe });
    }
    if (m.category) {
      const cat = this.columnOption(w.categoryColumnId)?.name;
      rows.push({ label: t('widgetEditor.fields.categoryColumnId'), value: cat ?? '-', missing: !cat });
    }
    if (m.aggregate && (m.family === 'chart' || w.defaultView !== 'LVT')) {
      const col = this.columnOption(w.aggregateColumnId)?.name;
      const op = w.aggregateOperator ? t('widgetEditor.operators.' + w.aggregateOperator) : null;
      rows.push({
        label: t('widgetEditor.fields.aggregateColumnId'),
        value: col ? (op ? op + ' \u00b7 ' + col : col) : '-',
        missing: !col || !op,
      });
    }
    if (src === 'tab' || src === 'table') {
      const n = this.filterPhrases().length;
      rows.push({ label: t('widgetEditor.filters'), value: n ? t('widgetEditor.filterCount', { count: n }) : t('widgetEditor.noFilter') });
    }
    return rows;
  });
  /** section at the top of the form (scroll position) */
  readonly activeSection = signal<EditorSection>('type');
  /** display options folded (a new widget shows only what is needed first) */
  readonly displayOpen = signal(true);
  readonly displayShown = computed(
    () => this.displayOpen() || (this.showErrors() && SECTION_FIELDS.display.some((f) => !!this.errors()[f]))
  );
  /** the display options in a few words, shown when folded */
  readonly displaySummary = computed(() => {
    const w = this.draft();
    const m = this.meta();
    if (!w) return [];
    const t = (key: string, params?: object) => this.translateService.instant(key, params);
    const parts: string[] = [];
    if (m.format) {
      if (w.measureUnit) parts.push(w.measureUnit);
      parts.push(t('widgetEditor.summary.decimals', { count: w.precision ?? 2 }));
      parts.push(t('widgetEditor.formats.' + (this.display().format ?? 'number')));
    }
    if (m.thresholds) parts.push(t(this.levelRanges().length ? 'widgetEditor.summary.levels' : 'widgetEditor.summary.noLevels'));
    if (m.family !== 'generic') {
      const r = w.refreshRate || 0;
      parts.push(
        r
          ? t('widgetEditor.summary.refresh', { value: REFRESH_PRESETS.includes(r) ? t('widgetEditor.refresh.' + r) : r + ' s' })
          : t('widgetEditor.summary.noRefresh')
      );
    }
    return parts;
  });
  readonly errorCount = computed(() => Object.keys(this.errors()).length);
  readonly dirty = computed(() => isDirty(this.draft(), this.#saved()) || this.rolesChanged());
  readonly typeChoices = computed(() =>
    this.types.filter((t) => {
      // a saved view keeps its source: only the types able to read it
      if (this.savedView()) return t.sources.includes(this.source()) && (t.family !== 'grid' || this.draft()?.tabId > 0);
      return t.family !== 'generic' || this.isAdmin || this.meta().family === 'generic';
    })
  );

  /** lookups */
  readonly widgetOptions = signal<ComboOption[]>([]);
  readonly windowOption = signal<ComboOption | null>(null);
  readonly tableOption = signal<ComboOption | null>(null);
  readonly tabs = signal<EditorTab[]>([]);
  readonly sourceColumns = signal<EditorColumn[]>([]);
  readonly columnsLoading = signal(false);
  readonly tabOptions = computed<ComboOption[]>(() =>
    this.tabs().map((t) => ({ id: t.id, name: t.level > 0 ? `${'  '.repeat(t.level)}${t.name}` : t.name }))
  );
  /** columns with a marker of what they hold (number, date, text...); for the value, numbers first */
  readonly columnOptions = computed<ComboOption[]>(() => this.sourceColumns().map((c) => this.#columnOption(c)));
  readonly valueColumnOptions = computed<ComboOption[]>(() => valueColumnsFirst(this.sourceColumns()).map((c) => this.#columnOption(c)));
  readonly aggregateColumn = computed(() => this.sourceColumns().find((c) => c.id === this.draft()?.aggregateColumnId) ?? null);
  readonly operators = computed<AggregateOperator[]>(() => operatorsFor(this.aggregateColumn(), this.draft()?.widgetType));
  readonly genericComponents = computed<ComboOption[]>(() =>
    Array.from(CacheManagerService.iupics_widgets.keys())
      .filter((k) => !TYPED_COMPONENTS.includes(k))
      .map((k) => ({ id: k, name: k.replace(/(Ui)?Component$/, '').replace(/([a-z])([A-Z])/g, '$1 $2') }))
  );

  /** roles (administrators): offered, selected, changed */
  readonly roleOptions = signal<{ id: number; name: string }[]>([]);
  readonly selectedRoles = signal<Set<number>>(new Set());
  readonly rolesChanged = signal(false);
  readonly roleFilter = signal('');
  readonly shownRoles = computed(() => {
    const q = this.roleFilter().trim().toLowerCase();
    return q ? this.roleOptions().filter((r) => r.name.toLowerCase().includes(q)) : this.roleOptions();
  });

  /** universal filter of the source (not for a saved view: its filters are kept) */
  readonly showFilters = signal(false);
  searchColumns: unknown[] = null;
  readonly updateFieldUFEmitter = new EventEmitter<CompiereDataGridRequestJSON>();

  /** preview */
  /** display options of the draft (chart options, number format, alert direction) */
  readonly display = computed<WidgetDisplayOptions>(() => parseDisplayOptions(this.draft()?.displayOptions));
  readonly formats: WidgetNumberFormat[] = ['number', 'currency', 'compact', 'percent'];
  readonly legends: WidgetLegend[] = ['auto', 'show', 'hide'];
  /** chart with axes (titles, stacking) */
  readonly cartesian = computed(() => ['CLC', 'BRC', 'LIC', 'SCC', 'BUC'].includes(this.draft()?.widgetType));
  readonly canStack = computed(() => ['CLC', 'BRC', 'LIC'].includes(this.draft()?.widgetType));
  /** alert direction: chosen, or what the order of the levels gives */
  readonly direction = computed<WidgetAlertDirection>(() => {
    const w = this.draft();
    return this.display().alertDirection ?? ((w?.alertLevel ?? 0) >= (w?.normalLevel ?? 0) ? 'higherWorse' : 'higherBetter');
  });
  /** the three ranges of a count with their colour, with the real numbers ("up to 10", "10 to 50", "from 50") */
  readonly levelRanges = computed(() => {
    const w = this.draft();
    const normal = w?.normalLevel ?? 0;
    const alert = w?.alertLevel ?? 0;
    if (!normal && !alert) return [];
    const f = (v: number) => formatNumber(v, { format: this.display().format, currency: this.display().currency, precision: w.precision, unit: w.measureUnit }, this.#locale);
    const low = Math.min(normal, alert);
    const high = Math.max(normal, alert);
    // lowest range takes the colour of a value just below the lower level
    const lowState = levelState(low - 1, normal, alert, this.display().alertDirection);
    const highState = levelState(high + 1, normal, alert, this.display().alertDirection);
    const color = (s: string) => (s === 'normal' ? w.normalColor : s === 'alert' ? w.alertColor : w.warningColor);
    return [
      { key: 'widgetEditor.rangeUpTo', params: { value: f(low) }, color: color(lowState), state: lowState },
      { key: 'widgetEditor.rangeBetween', params: { from: f(low), to: f(high) }, color: w.warningColor, state: 'warning' },
      { key: 'widgetEditor.rangeFrom', params: { value: f(high) }, color: color(highState), state: highState },
    ];
  });
  /** the filters of the widget in words */
  readonly filterPhrases = computed<FilterPhrase[]>(() =>
    describeFilters(this.draft()?.gridRequest, (col) => this.sourceColumns().find((c) => c.columnName === col)?.name ?? null)
  );
  readonly info = signal<EditorInfo | null>(null);
  /** shared roles that would see the widget empty */
  readonly rolesWithoutAccess = signal<number[]>([]);
  /** preview frame: as wide as the panel, or at a dashboard card size */
  readonly previewSize = signal<'auto' | 'small' | 'wide' | 'tall'>('auto');
  readonly previewSizes = ['auto', 'small', 'wide', 'tall'] as const;
  /** small screens: the preview can be folded */
  readonly previewOpen = signal(true);
  /** dashboard tabs a saved widget can be added to (editor opened from the menu) */
  readonly dashboardTabs = signal<IupicsDashboardCategory[]>([]);
  readonly addTabId = signal<number | null>(null);
  /** administrators: the list of every widget */
  readonly view = signal<'editor' | 'library'>('editor');
  readonly library = signal<LibraryWidget[] | null>(null);
  readonly libraryLoading = signal(false);
  readonly libraryFilter = signal('');
  readonly librarySelection = signal<Set<number>>(new Set());
  /** selected rows among the shown ones (what Delete acts on) */
  readonly shownSelection = computed(() => {
    const sel = this.librarySelection();
    return this.shownLibrary().filter((r) => sel.has(r.id)).length;
  });
  readonly shownLibrary = computed(() => {
    const q = this.libraryFilter().trim().toLowerCase();
    const rows = this.library() ?? [];
    return q ? rows.filter((r) => [r.name, r.userName, r.roleName, ...r.sharedRoles].some((x) => x?.toLowerCase().includes(q))) : rows;
  });
  #locale: string | undefined;
  #rolesTimer: ReturnType<typeof setTimeout> | undefined;
  #rolesSub: Subscription | undefined;
  readonly #dashboards = inject(DashboardService);

  readonly previewMissing = computed(() => {
    const e = this.errors();
    return PREVIEW_FIELDS.filter((f) => !!e[f]);
  });
  #preview: ComponentRef<AbstractWidgetComponent> | null = null;
  #previewClass: string | null = null;
  #previewKey: string | null = null;
  #previewTimer: ReturnType<typeof setTimeout> | undefined;
  #subs: Subscription[] = [];
  #sourceSub: Subscription | undefined;
  #openSub: Subscription | undefined;
  #askSubs: Subscription[] = [];
  #previewSub: Subscription | undefined;
  /** the user clicked or typed in the preview: a grid layout it reports is a change of theirs */
  #previewTouched = false;
  #columnsSub: Subscription | undefined;
  #filterSub: Subscription | undefined;

  //#region life cycle (custom screen: no dictionary fields to build)
  override ngOnInit(): void {
    if (!this.isModal && this.activeTab) {
      this.workspaceService.addLinkedComponentToTabMap(this.activeTab.id, this);
    }
    const account = this.connectorService.getIupicsUserAccount();
    this.#owner = { roleId: account?.current_role?.role_id ?? 0, userId: account?.id ?? 0 };
    this.isAdmin = !!account?.current_role?.isAdministrator;
    this.#loadWidgetList();
    this.#loadRoleOptions();
    this.#locale = this.connectorService.getIupicsDefaultLanguage()?.iso_code?.replace('_', '-');
    this.#subs.push(this.#lookup.info().pipe(catchError(() => of(null))).subscribe((info) => this.info.set(info)));
    if (!this.isModal) this.#loadDashboardTabs();
    const startId = this.widget?.id || this.gridPref?.widgetID;
    // a new widget at once (the page is never empty), replaced when the widget to open is found
    if (startId > 0) {
      this.draft.set(this.#newWidget());
      this.#saved.set(cloneDeep(this.draft()));
      this.#open(startId);
    } else {
      this.#edit(this.#newWidget());
    }
  }

  override ngAfterViewInit(): void {
    // the base one only registers the tab (done in ngOnInit); here: the section bar follows the scroll
    const body = this.bodyRef?.nativeElement;
    if (!body) return;
    this.#zone.runOutsideAngular(() => {
      let frame = 0;
      const onScroll = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.#spySection(body));
      };
      body.addEventListener('scroll', onScroll, { passive: true });
      this.#unlistenScroll = () => {
        cancelAnimationFrame(frame);
        body.removeEventListener('scroll', onScroll);
      };
    });
  }

  /** the last section whose title passed under the bar (the last one at the bottom of the page) */
  #spySection(body: HTMLElement): void {
    const sections = Array.from(body.querySelectorAll<HTMLElement>('[data-section]'));
    if (!sections.length) return;
    const limit = body.getBoundingClientRect().top + 64;
    let current = sections[0].dataset['section'] as EditorSection;
    if (body.scrollTop + body.clientHeight >= body.scrollHeight - 4) {
      current = sections[sections.length - 1].dataset['section'] as EditorSection;
    } else {
      for (const s of sections) {
        if (s.getBoundingClientRect().top <= limit) current = s.dataset['section'] as EditorSection;
      }
    }
    if (current !== this.activeSection()) this.#zone.run(() => this.activeSection.set(current));
  }

  goToSection(id: EditorSection): void {
    if (id === 'display') this.displayOpen.set(true);
    this.activeSection.set(id);
    setTimeout(() => {
      const el = this.bodyRef?.nativeElement.querySelector<HTMLElement>(`[data-section="${id}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /** from the preview checklist: to the field to fill */
  focusField(field: string): void {
    if (SECTION_FIELDS.display.includes(field)) this.displayOpen.set(true);
    setTimeout(() => {
      const root = this.specificContainer?.nativeElement as HTMLElement | undefined;
      const el = root?.querySelector<HTMLElement>(`[data-field="${field}"]`);
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el?.focus({ preventScroll: true });
    });
  }

  override buildWindow(): void {}

  override ngOnDestroy(): void {
    this.#unlistenScroll?.();
    clearTimeout(this.#previewTimer);
    clearTimeout(this.#rolesTimer);
    this.#rolesSub?.unsubscribe();
    this.#subs.forEach((s) => s.unsubscribe());
    this.#sourceSub?.unsubscribe();
    this.#columnsSub?.unsubscribe();
    this.#filterSub?.unsubscribe();
    this.#openSub?.unsubscribe();
    // an answer given after the editor closed does nothing
    this.#askSubs.forEach((s) => s.unsubscribe());
    this.#destroyPreview();
    super.ngOnDestroy();
  }
  //#endregion

  //#region open, new, duplicate
  #newWidget(): IupicsWidget {
    const theme = this.#themes.active()?.data ?? {};
    const colors = {
      normal: theme['--success-bg-color-active'] || DEFAULT_COLORS.normal,
      warning: theme['--warning-bg-color-active'] || DEFAULT_COLORS.warning,
      alert: theme['--alert-bg-color-active'] || DEFAULT_COLORS.alert,
    };
    // from a saved view of a grid: its source, filters and layout
    const p = this.gridPref;
    if (!p) {
      // the type and source used last (a series of widgets on the same data is quicker)
      return this.#withLastUsed(newWidget(this.#owner, colors));
    }
    return newWidget(
      this.#owner,
      colors,
      p
        ? {
            name: p.name,
            gridRequest: p.gridRequest,
            gridState: p.gridState,
            userGridPreferenceId: p.userGridPreferenceID,
            tabId: p.tabID,
            tableId: p.tableID,
            windowId: p.windowID,
          }
        : undefined
    );
  }

  /** a saved widget, from the list (loaded first when needed) */
  #open(id: number): void {
    const known = this.#widgets.getIupicsWidget(id);
    const found$: Observable<IupicsWidget | undefined> = known
      ? of(known)
      : this.#widgets.getIupicsWidgets().pipe(map((list) => list.find((w) => w.id == id) ?? (this.widget?.id == id ? this.widget : undefined)));
    this.#openSub?.unsubscribe();
    this.#openSub = found$
      .pipe(
        // administrators open widgets of other users from the library: not in their own list
        switchMap((w) => (w || !this.isAdmin ? of(w) : this.#lookup.libraryWidget(id))),
        catchError(() => of(undefined))
      )
      .subscribe((w) => {
        if (!w) {
          this.#notify('generic.error', 'widgetEditor.widgetNotFound', 'error');
          this.#edit(this.#newWidget());
          return;
        }
        const opened = cloneDeep(w);
        // the widget of this saved view: keep the link even when the widget list was read before the view pointed to it
        if (this.gridPref?.widgetID == opened.id && !(opened.userGridPreferenceId > 0) && this.gridPref.userGridPreferenceID > 0) {
          opened.userGridPreferenceId = this.gridPref.userGridPreferenceID;
        }
        this.#edit(opened);
        this.#loadWidgetRoles(w.id);
      });
  }

  /** start editing a widget (unsaved changes already confirmed) */
  #edit(w: IupicsWidget): void {
    this.#previewTouched = false;
    // a new widget starts with the essentials: display options folded (summary shown)
    this.displayOpen.set(w.id > 0);
    this.draft.set(w);
    this.#saved.set(cloneDeep(w));
    this.showErrors.set(false);
    this.#touched.set(new Set());
    this.rolesChanged.set(false);
    if (!(w.id > 0)) this.selectedRoles.set(new Set());
    this.#loadSource();
    this.#renderPreview(true);
  }

  pickWidget(option: ComboOption | null): void {
    const id = option ? Number(option.id) : 0;
    if (id === (this.draft()?.id ?? 0)) return;
    this.#confirmDiscard(() => (id > 0 ? this.#open(id) : this.#edit(this.#newWidget())));
  }

  newOne(): void {
    this.#confirmDiscard(() => this.#edit(this.#newWidget()));
  }

  duplicateOne(): void {
    const w = this.draft();
    if (!w) return;
    this.#confirmDiscard(() => {
      const copy = duplicate(w, this.#owner, this.translateService.instant('widgetEditor.copySuffix'));
      this.#edit(copy);
      // a copy is new: it is saved only on demand
      this.#saved.set(null);
    });
  }

  currentWidgetOption(): ComboOption | null {
    const w = this.draft();
    return w?.id > 0 ? (this.widgetOptions().find((o) => o.id === w.id) ?? { id: w.id, name: w.name ?? '' }) : null;
  }

  #loadWidgetList(): void {
    this.#subs.push(
      this.#widgets
        .getIupicsWidgets()
        .pipe(catchError(() => of([] as IupicsWidget[])))
        .subscribe((list) =>
          this.widgetOptions.set(
            (list ?? [])
              .map((w) => ({ id: w.id, name: w.name ?? `#${w.id}`, icon: typeMeta(w.widgetType).icon, hint: this.typeLabel(w.widgetType) }))
              .sort((a, b) => a.name.localeCompare(b.name))
          )
        )
    );
  }

  typeIcon(code: string): string {
    return typeMeta(code).icon;
  }

  typeLabel(code: string): string {
    return this.translateService.instant(`widgetEditor.types.${code}`);
  }
  //#endregion

  //#region changes
  /** a change of the form: draft updated, preview refreshed after a pause */
  update(patch: Partial<IupicsWidget>, ...fields: string[]): void {
    this.draft.update((w) => ({ ...w, ...patch }));
    if (fields.length) this.#touch(...fields);
    this.#schedulePreview();
  }

  #touch(...fields: string[]): void {
    this.#touched.update((s) => new Set([...s, ...fields]));
  }

  /** problem of a field, once shown (after a save attempt or a change of that field) */
  error(field: string): string | null {
    const e = this.errors()[field];
    return e && (this.showErrors() || this.#touched().has(field)) ? e : null;
  }

  setType(meta: WidgetTypeMeta): void {
    const w = this.draft();
    if (w.widgetType === meta.code) return;
    const sourceBefore = sourceOf(w);
    const next = applyType(w, meta.code, this.aggregateColumn());
    this.draft.set(next);
    if (sourceOf(next) !== sourceBefore || next.tabId !== w.tabId || next.tableId !== w.tableId) this.#loadSource();
    // columns needed by the new type (none were read for a grid or a web page); cached by the service
    else this.#loadColumns();
    this.#renderPreview(true);
  }

  /** arrows move in the type picker (radio group) */
  onTypeKey(event: KeyboardEvent, index: number): void {
    const list = this.typeChoices();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = list[(index + step + list.length) % list.length];
    this.setType(next);
    const root = (event.currentTarget as HTMLElement | null)?.parentElement;
    setTimeout(() => (root?.querySelector(`[data-type="${next.code}"]`) as HTMLElement | null)?.focus());
  }

  setSourceKind(kind: WidgetSource): void {
    if (this.source() === kind) return;
    this.draft.set(setSource(this.draft(), kind));
    this.#loadSource();
    this.#renderPreview(true);
  }

  pickWindow(option: ComboOption | null): void {
    const windowId = option ? Number(option.id) : null;
    this.windowOption.set(option);
    this.draft.set(clearColumns({ ...this.draft(), windowId, tabId: null, gridRequest: null, gridState: null }));
    this.#touch('windowId');
    this.sourceColumns.set([]);
    this.#resetFilters();
    // may pick the first tab at once (tabs cached), which reads its columns
    this.#loadTabs(windowId, true);
    this.#renderPreview(true);
  }

  pickTab(option: ComboOption | null): void {
    const tabId = option ? Number(option.id) : null;
    this.draft.set(clearColumns({ ...this.draft(), tabId, gridRequest: null, gridState: null }));
    this.#touch('tabId');
    this.#loadColumns();
    this.#resetFilters();
    this.#checkRoles();
    this.#renderPreview(true);
  }

  pickTable(option: ComboOption | null): void {
    this.tableOption.set(option);
    this.draft.set(clearColumns({ ...this.draft(), tableId: option ? Number(option.id) : null, gridRequest: null, gridState: null }));
    this.#touch('tableId');
    this.#loadColumns();
    this.#resetFilters();
    this.#checkRoles();
    this.#renderPreview(true);
  }

  pickColumn(field: 'categoryColumnId' | 'serieColumnId' | 'aggregateColumnId', option: ComboOption | null): void {
    const id = option ? Number(option.id) : null;
    const patch: Partial<IupicsWidget> = { [field]: id };
    if (!id) {
      if (field === 'categoryColumnId') patch.categorySort = null;
      if (field === 'serieColumnId') patch.serieSort = null;
      if (field === 'aggregateColumnId') patch.aggregateSort = null;
    }
    if (field === 'aggregateColumnId') {
      // operator kept when the new column allows it, else the first allowed one (count for a text column)
      const column = this.sourceColumns().find((c) => c.id === id) ?? null;
      const allowed = operatorsFor(column, this.draft().widgetType);
      const current = this.draft().aggregateOperator as AggregateOperator;
      patch.aggregateOperator = id ? (allowed.includes(current) ? current : allowed.includes('SUM') ? 'SUM' : allowed[0]) : null;
    }
    this.update(patch, field);
  }

  columnOption(id: number | null): ComboOption | null {
    if (!id) return null;
    return this.columnOptions().find((o) => o.id === id) ?? { id, name: `#${id}` };
  }

  tabOption(): ComboOption | null {
    const id = this.draft()?.tabId;
    if (!id) return null;
    return this.tabOptions().find((o) => o.id === id) ?? { id, name: `#${id}` };
  }

  genericOption(): ComboOption | null {
    const cls = this.draft()?.angularClass;
    return cls ? (this.genericComponents().find((o) => o.id === cls) ?? { id: cls, name: cls }) : null;
  }

  pickGeneric(option: ComboOption | null): void {
    this.update({ angularClass: option ? String(option.id) : '' }, 'angularClass');
    this.#renderPreview(true);
  }

  setSort(field: 'categorySort' | 'serieSort' | 'aggregateSort', value: string): void {
    this.update({ [field]: value === 'ASC' || value === 'DSC' ? value : null });
  }

  numberValue(value: string): number | null {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(String(value).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  setRefresh(seconds: number | null): void {
    const value = seconds && seconds > 0 ? Math.max(Math.round(seconds), MIN_REFRESH_SECONDS) : 0;
    this.update({ refreshRate: value }, 'refreshRate');
  }

  windowSearch = (q: string) => this.#lookup.windows(q);
  tableSearch = (q: string) => this.#lookup.tables(q);
  //#endregion

  //#region source lookups
  /** names and lists of the current source (opening a widget, another type or source) */
  #loadSource(): void {
    const w = this.draft();
    this.#sourceSub?.unsubscribe();
    this.windowOption.set(null);
    this.tableOption.set(null);
    this.tabs.set([]);
    this.sourceColumns.set([]);
    if (w.windowId > 0) {
      this.#sourceSub = this.#lookup
        .windows('', [w.windowId])
        .pipe(catchError(() => of([] as ComboOption[])))
        .subscribe((found) => this.windowOption.set(found[0] ?? { id: w.windowId, name: `#${w.windowId}` }));
      this.#loadTabs(w.windowId, false);
    } else if (w.tableId > 0) {
      this.#sourceSub = this.#lookup
        .tables('', [w.tableId])
        .pipe(catchError(() => of([] as ComboOption[])))
        .subscribe((found) => this.tableOption.set(found[0] ?? { id: w.tableId, name: `#${w.tableId}` }));
    }
    this.#loadColumns();
    this.#resetFilters();
    this.#checkRoles();
  }

  /** tabs of the window; the first one picked when the window was just chosen */
  #loadTabs(windowId: number | null, pickFirst: boolean): void {
    this.tabs.set([]);
    if (!(windowId > 0)) return;
    this.#subs.push(
      this.#lookup
        .tabs(windowId)
        .pipe(catchError(() => of([] as EditorTab[])))
        .subscribe((tabs) => {
          if (this.draft()?.windowId !== windowId) return;
          this.tabs.set(tabs);
          if (pickFirst && tabs.length && !this.draft().tabId) this.pickTab({ id: tabs[0].id, name: tabs[0].name });
        })
    );
  }

  #loadColumns(): void {
    const w = this.draft();
    const meta = typeMeta(w.widgetType);
    this.#columnsSub?.unsubscribe();
    this.sourceColumns.set([]);
    if (!meta.aggregate && !meta.category) return;
    const tabId = w.tabId > 0 ? w.tabId : null;
    const tableId = !tabId && w.tableId > 0 ? w.tableId : null;
    if (!tabId && !tableId) return;
    this.columnsLoading.set(true);
    this.#columnsSub = this.#lookup
      .columns(tabId, tableId)
      .pipe(
        catchError(() => of([] as EditorColumn[])),
        finalize(() => this.columnsLoading.set(false))
      )
      .subscribe((columns) => this.sourceColumns.set(columns));
  }
  //#endregion

  //#region filters of the source (universal filter)
  #resetFilters(): void {
    this.#filterSub?.unsubscribe();
    this.showFilters.set(false);
    this.searchColumns = null;
    this.#cd.detectChanges();
    const w = this.draft();
    if (!w || fromSavedView(w) || typeMeta(w.widgetType).family === 'web' || typeMeta(w.widgetType).family === 'generic') return;
    let ready$: Observable<boolean> | null = null;
    if (w.tableId > 0 && !(w.tabId > 0)) {
      ready$ = this.#widgets.getSearchColumns(w).pipe(
        map((columns) => {
          this.searchColumns = columns?.length ? columns : null;
          return !!this.searchColumns;
        })
      );
    } else if (w.windowId > 0 && w.tabId > 0) {
      ready$ = this.uiCreator.getWindow(w.windowId).pipe(map(() => true));
    }
    if (!ready$) return;
    this.#filterSub = ready$.pipe(catchError(() => of(false))).subscribe((ok) => {
      if (!ok) return;
      this.showFilters.set(true);
      this.#cd.detectChanges();
      const request = this.draft()?.gridRequest;
      if (request) {
        try {
          this.updateFieldUFEmitter.emit(JSON.parse(request));
        } catch {
          // unreadable stored filters: shown empty
        }
      }
    });
  }

  onFilterChange(result: { filterToApply: CompiereDataGridRequestJSON; isNotFromUF: boolean }): void {
    const w = this.draft();
    if (fromSavedView(w) || result.isNotFromUF) return;
    const req = result.filterToApply;
    if (w.tabId > 0) {
      req.entityId = w.tabId;
      req.windowType = CompiereDataGridType.WINDOW;
    } else if (w.tableId > 0 && this.searchColumns) {
      req.tableName = (this.searchColumns[0] as { field?: { tableName?: string } })?.field?.tableName;
      req.windowType = CompiereDataGridType.TABLE;
    }
    this.update({ gridRequest: JSON.stringify(req) });
  }
  //#endregion

  //#region roles (administrators)
  #loadRoleOptions(): void {
    if (!this.isAdmin) return;
    this.#subs.push(
      this.#widgets
        .getAvailableWidgetRoles()
        .pipe(catchError(() => of([] as { id: number; name: string }[])))
        .subscribe((roles) => this.roleOptions.set(roles ?? []))
    );
  }

  #loadWidgetRoles(widgetId: number): void {
    this.selectedRoles.set(new Set());
    this.rolesChanged.set(false);
    if (!this.isAdmin || !(widgetId > 0)) return;
    this.#subs.push(
      this.#widgets
        .getWidgetRoles(this.isAdmin, widgetId)
        .pipe(catchError(() => of([] as number[])))
        .subscribe((ids) => {
          // a pick made while loading is kept
          if (this.draft()?.id === widgetId && !this.rolesChanged()) {
            this.selectedRoles.set(new Set(ids ?? []));
            this.#checkRoles();
          }
        })
    );
  }

  toggleRole(id: number, on: boolean): void {
    this.selectedRoles.update((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
    this.rolesChanged.set(true);
    this.#checkRoles();
  }

  roleName(id: number): string {
    return this.roleOptions().find((r) => r.id === id)?.name ?? `#${id}`;
  }

  /** after a change of roles or source: the shared roles that cannot read it (they would see it empty) */
  #checkRoles(): void {
    clearTimeout(this.#rolesTimer);
    const roleIds = Array.from(this.selectedRoles());
    const w = this.draft();
    if (!this.isAdmin || !roleIds.length || !w || (!(w.tabId > 0) && !(w.tableId > 0))) {
      this.rolesWithoutAccess.set([]);
      return;
    }
    this.#rolesTimer = setTimeout(() => {
      // only the last check counts
      this.#rolesSub?.unsubscribe();
      this.#rolesSub = this.#lookup
        .rolesWithoutAccess(this.draft(), roleIds)
        .pipe(catchError(() => of([] as number[])))
        .subscribe((ids) => this.rolesWithoutAccess.set(ids ?? []));
    }, 500);
  }
  //#endregion

  //#region save, delete, close
  onKey(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      this.save();
    }
  }

  save(then?: (saved: IupicsWidget) => void): void {
    if (this.saving() || !this.draft()) return;
    if (this.columnsLoading()) {
      // the calculation is checked against the column type once the columns are read
      this.#notify('generic.warning', 'widgetEditor.waitColumns', 'warning');
      return;
    }
    this.showErrors.set(true);
    if (this.errorCount() > 0) {
      this.focusFirstError();
      return;
    }
    const w = this.draft();
    const toSave: IupicsWidget = {
      ...w,
      name: w.name.trim(),
      iframe: w.iframe?.trim() || null,
      newWidgetRoles: this.isAdmin && this.rolesChanged() ? Array.from(this.selectedRoles()) : undefined,
    };
    this.saving.set(true);
    this.#subs.push(
      this.#widgets
        .saveWidget(toSave)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: (saved) => {
            if (!(saved?.id > 0)) {
              this.#notify('generic.error', 'widgetEditor.saveFailed', 'error');
              return;
            }
            this.widget = saved;
            this.draft.set(cloneDeep(saved));
            this.#saved.set(cloneDeep(saved));
            this.selectedRoles.set(new Set(saved.widgetRoles ?? []));
            this.rolesChanged.set(false);
            this.showErrors.set(false);
            this.#touched.set(new Set());
            this.#notify('generic.success', 'widgetEditor.saveSuccess', 'success');
            this.#loadWidgetList();
            this.#rememberLastUsed(saved);
            this.updateInitialUFEmitter.emit(saved.id);
            then?.(saved);
          },
          error: (e) => this.#notify('generic.error', this.#backendMessage(e, 'widgetEditor.saveFailed'), 'error', true),
        })
    );
  }

  /** focus the first field to fix (in the order of the page) */
  focusFirstError(): void {
    this.showErrors.set(true);
    setTimeout(() => {
      const root = this.specificContainer?.nativeElement as HTMLElement | undefined;
      const fields = Object.keys(this.errors());
      const target = Array.from(root?.querySelectorAll<HTMLElement>('[data-field]') ?? []).find((el) => fields.includes(el.dataset['field']));
      target?.focus();
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  deleteOne(): void {
    const w = this.draft();
    if (!(w?.id > 0)) return;
    this.#ask('widgetEditor.deleteConfirm', { name: w.name ?? '' }, () =>
      this.#subs.push(
        this.#widgets.deleteWidget(w.id).subscribe({
          next: (ok) => {
            if (!ok) {
              this.#notify('generic.error', 'widgetEditor.deleteFailed', 'error');
              return;
            }
            this.#clearGridPreference(w);
            if (this.gridPref?.widgetID === w.id) this.gridPref.widgetID = null;
            this.widget = null;
            this.#loadWidgetList();
            this.updateInitialUFEmitter.emit(0);
            this.#edit(this.#newWidget());
          },
          error: (e) => this.#notify('generic.error', this.#backendMessage(e, 'widgetEditor.deleteFailed'), 'error', true),
        })
      )
    );
  }

  close(): void {
    this.#confirmDiscard(() => this.closeModalEmitter.emit());
  }

  #clearGridPreference(w: IupicsWidget): void {
    if (w.userGridPreferenceId > 0) {
      const preference = this.#gridPreferences.findGridPreferenceById(w.userGridPreferenceId);
      if (preference) {
        preference.widgetID = null;
        this.#gridPreferences.setGridPreferenceFromMap(preference);
      }
    }
  }

  /** run now, or after confirming that the unsaved changes can be dropped */
  #confirmDiscard(then: () => void): void {
    if (!this.dirty()) {
      then();
      return;
    }
    this.#ask('widgetEditor.discardConfirm', {}, then);
  }

  #ask(key: string, params: object, onYes: () => void): void {
    // one question at a time (a second click would answer twice)
    if (this.#askSubs.length) return;
    Global.infoDialog.message = {
      summary: this.translateService.instant('generic.warning'),
      detail: this.translateService.instant(key, params),
    };
    Global.infoDialog.dialogType = InfoDialogType.CONFIRM_YESNO;
    Global.infoDialog.showInfoDialog();
    const done = () => {
      this.#askSubs.forEach((s) => s.unsubscribe());
      this.#askSubs = [];
    };
    this.#askSubs = [
      Global.infoDialog.confirm.subscribe(() => {
        done();
        onYes();
      }),
      Global.infoDialog.cancel.subscribe(() => done()),
    ];
  }

  #notify(summaryKey: string, detail: string, kind: 'success' | 'error' | 'warning', detailIsText = false): void {
    this.messageManager.newMessage(
      new IupicsMessage(this.translateService.instant(summaryKey), detailIsText ? detail : this.translateService.instant(detail), kind)
    );
  }

  /** message of the server (JSON body, as text or object), else the translated fallback */
  #backendMessage(error: unknown, fallbackKey: string): string {
    let body = (error as { error?: unknown })?.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        body = null;
      }
    }
    const message = (body as { message?: unknown })?.message;
    return typeof message === 'string' && message.trim() ? message : this.translateService.instant(fallbackKey);
  }
  //#endregion

  //#region preview
  #schedulePreview(): void {
    clearTimeout(this.#previewTimer);
    this.#previewTimer = setTimeout(() => this.#renderPreview(false), PREVIEW_DELAY_MS);
  }

  refreshPreview(): void {
    this.#renderPreview(true);
  }

  /**
   * The widget component of the type, fed with a copy of the draft. Data read again only when a data field changed
   * (or forced); colours, unit, levels... applied to the data already read.
   */
  #renderPreview(force: boolean): void {
    clearTimeout(this.#previewTimer);
    const w = this.draft();
    const component = w?.angularClass ? CacheManagerService.iupics_widgets.get(w.angularClass) : null;
    if (!component || this.previewMissing().length) {
      this.#destroyPreview();
      return;
    }
    const copy = cloneDeep(w);
    const key = dataKey(w);
    if (!this.#preview || this.#previewClass !== w.angularClass) {
      this.#destroyPreview();
      const ref = this.previewHost.createComponent<AbstractWidgetComponent>(component);
      ref.instance.isPreview = true;
      (ref.instance as unknown as { activeView?: string }).activeView = w.defaultView ?? CountListViewType.COUNT_VIEW;
      ref.instance.widget = copy;
      // the grid widget reports its column layout (also while it sets itself up): kept once the user changed it
      this.#previewTouched = false;
      this.#previewSub = ref.instance.widgetChanged.subscribe((changed: IupicsWidget) => {
        const gridState = changed?.gridState ?? null;
        if (this.#previewTouched && gridState !== (this.draft()?.gridState ?? null)) this.update({ gridState });
      });
      this.#preview = ref;
      this.#previewClass = w.angularClass;
    } else if (force || key !== this.#previewKey) {
      this.#preview.instance.widget = copy;
    } else {
      this.#preview.instance.applyPresentation(copy);
    }
    this.#previewKey = key;
    this.#preview.changeDetectorRef.detectChanges();
  }

  onPreviewInput(): void {
    this.#previewTouched = true;
  }

  #destroyPreview(): void {
    this.#previewSub?.unsubscribe();
    this.#previewSub = undefined;
    this.previewHost?.clear();
    this.#preview = null;
    this.#previewClass = null;
    this.#previewKey = null;
  }
  //#endregion

  //#region display options
  setDisplay(patch: Partial<WidgetDisplayOptions>): void {
    const options = { displayOptions: serializeDisplayOptions({ ...this.display(), ...patch }) };
    // only the alert direction is checked with the levels
    if ('alertDirection' in patch) this.update(options, 'alertLevel');
    else this.update(options);
  }

  #columnOption(c: EditorColumn): ComboOption {
    const kind = columnKind(c.displayType);
    return { id: c.id, name: c.name, hint: this.translateService.instant(`widgetEditor.kinds.${kind}`), icon: COLUMN_KIND_ICONS[kind] };
  }
  //#endregion

  //#region last type and source used (per user, this browser)
  #lastUsedKey(): string {
    return 'apiz.widgetEditor.' + this.#owner.userId;
  }

  #rememberLastUsed(w: IupicsWidget): void {
    if (fromSavedView(w)) return;
    try {
      localStorage.setItem(
        this.#lastUsedKey(),
        JSON.stringify({ type: w.widgetType, source: sourceOf(w), windowId: w.windowId || null, tabId: w.tabId || null, tableId: w.tableId || null })
      );
    } catch {
      // storage blocked: nothing remembered
    }
  }

  #withLastUsed(w: IupicsWidget): IupicsWidget {
    let last: { type?: string; source?: WidgetSource; windowId?: number; tabId?: number; tableId?: number } = {};
    try {
      last = JSON.parse(localStorage.getItem(this.#lastUsedKey()) ?? '{}') ?? {};
    } catch {
      return w;
    }
    if (!last.type || !WIDGET_TYPES.some((t) => t.code === last.type)) return w;
    let next = applyType(w, last.type);
    if (last.source && typeMeta(last.type).sources.includes(last.source)) next = setSource(next, last.source);
    if (last.source === 'tab' && last.windowId > 0) next = { ...next, windowId: last.windowId, tabId: last.tabId > 0 ? last.tabId : null };
    if (last.source === 'table' && last.tableId > 0) next = { ...next, tableId: last.tableId };
    return next;
  }
  //#endregion

  //#region add to a dashboard (editor opened from the menu)
  #loadDashboardTabs(): void {
    this.#subs.push(
      this.#dashboards
        .getIupicsDashboardCats()
        .pipe(catchError(() => of([] as IupicsDashboardCategory[])))
        .subscribe((cats) => {
          // the role's shared dashboard tabs cannot be changed here
          const own = (cats ?? []).filter((c) => (c as { isDashboardMeta?: string }).isDashboardMeta !== 'Y');
          this.dashboardTabs.set(own);
          if (own.length && !this.addTabId()) this.addTabId.set(own[0].id);
        })
    );
  }

  saveAndAdd(): void {
    const tabId = this.addTabId();
    const tab = this.dashboardTabs().find((c) => c.id === tabId);
    if (!tab) return;
    this.save((saved) => {
      if (tab.items?.some((i) => i.widget?.id === saved.id)) {
        this.#notify('generic.success', 'widgetEditor.alreadyOnDashboard', 'success');
        return;
      }
      const lineNo = Math.max(0, ...(tab.items ?? []).map((i) => i.lineNo ?? 0)) + 1;
      this.#subs.push(
        this.#dashboards.saveDashboardItem(tab.id, { colNo: 1, lineNo, width: 1, height: 1, cssColor: null, widget: saved }).subscribe({
          next: (item) => {
            if (item?.id > 0) {
              tab.items = [...(tab.items ?? []), item];
              this.#dashboards.resetCachingData();
              this.#notify('generic.success', this.translateService.instant('widgetEditor.addedToDashboard', { name: tab.name }), 'success', true);
            } else {
              this.#notify('generic.error', 'widgetEditor.addFailed', 'error');
            }
          },
          error: () => this.#notify('generic.error', 'widgetEditor.addFailed', 'error'),
        })
      );
    });
  }
  //#endregion

  //#region library (administrators)
  /** the draft stays as it is behind the list: nothing to confirm until another widget is opened */
  showLibrary(): void {
    if (this.view() === 'library') return;
    this.view.set('library');
    this.loadLibrary();
  }

  showEditor(): void {
    this.view.set('editor');
  }

  loadLibrary(): void {
    this.libraryLoading.set(true);
    this.librarySelection.set(new Set());
    this.#subs.push(
      this.#lookup
        .library()
        .pipe(finalize(() => this.libraryLoading.set(false)))
        .subscribe({
          next: (rows) => this.library.set(rows ?? []),
          error: (e) => {
            this.library.set([]);
            this.#notify('generic.error', this.#backendMessage(e, 'widgetEditor.libraryFailed'), 'error', true);
          },
        })
    );
  }

  toggleLibraryRow(id: number, on: boolean): void {
    this.librarySelection.update((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  toggleLibraryAll(on: boolean): void {
    this.librarySelection.set(on ? new Set(this.shownLibrary().map((r) => r.id)) : new Set());
  }

  openFromLibrary(id: number): void {
    if (this.draft()?.id === id) {
      this.view.set('editor');
      return;
    }
    this.#confirmDiscard(() => {
      this.view.set('editor');
      this.#open(id);
    });
  }

  deleteSelected(): void {
    // only rows still shown (the filter may hide selected ones)
    const shown = new Set(this.shownLibrary().map((r) => r.id));
    const ids = Array.from(this.librarySelection()).filter((id) => shown.has(id));
    if (!ids.length) return;
    const used = (this.library() ?? []).filter((r) => ids.includes(r.id) && r.dashboardItems > 0).length;
    this.#ask('widgetEditor.deleteManyConfirm', { count: ids.length, used }, () =>
      this.#subs.push(
        this.#lookup.deleteWidgets(ids).subscribe({
          next: (r) => {
            this.#widgets.resetCachingData();
            this.#dashboards.resetCachingData();
            this.#gridPreferences.forgetWidgets(r?.deleted ?? ids);
            if (this.gridPref?.widgetID > 0 && (r?.deleted ?? ids).includes(this.gridPref.widgetID)) this.gridPref.widgetID = null;
            this.#notify('generic.success', this.translateService.instant('widgetEditor.deletedMany', { count: r?.deleted?.length ?? 0 }), 'success', true);
            if (ids.includes(this.draft()?.id)) this.#edit(this.#newWidget());
            this.#loadWidgetList();
            this.loadLibrary();
          },
          error: (e) => this.#notify('generic.error', this.#backendMessage(e, 'widgetEditor.deleteFailed'), 'error', true),
        })
      )
    );
  }
  //#endregion

  readonly WidgetViewType = WidgetViewType;
}
